/**
 * Shared helpers for reading JSON response schemas and model capabilities.
 * Used by the node drawer and the flow markdown generator so the "expected
 * outputs" derivation and temperature gating stay identical in both.
 */

export const joinType = (type) => (Array.isArray(type) ? type.join(" | ") : type)

// One-line description of a JSON-schema property (shallow, for readability).
export const describeType = (def) => {
	if (!def || typeof def !== "object") return "any"
	if (Array.isArray(def.enum)) {
		const values = def.enum.filter((v) => v !== null).map((v) => `${v}`)
		const nullable = def.enum.includes(null) ? " | null" : ""
		const shown = values.slice(0, 4).join(" | ")
		const more = values.length > 4 ? " | ..." : ""
		return `enum(${shown}${more})${nullable}`
	}
	const type = joinType(def.type)
	if (type === "array" || (Array.isArray(def.type) && def.type.includes("array"))) {
		const itemType = def.items ? describeType(def.items) : "any"
		return `array<${itemType}>`
	}
	return type || "object"
}

// Top-level properties of a response schema, in declared order.
export const summariseSchema = (schema) => {
	if (!schema || typeof schema !== "object") return []
	const props = schema.properties || (schema.items && schema.items.properties) || {}
	return Object.entries(props).map(([key, def]) => ({
		key,
		type: describeType(def),
		required: Array.isArray(schema.required) ? schema.required.includes(key) : false,
	}))
}

// gpt-5+ and the o-series reasoning models ignore the temperature parameter
// (they only run at the default).
export const modelSupportsTemperature = (model) => {
	if (!model) return true
	const m = model.toLowerCase()
	if (/^o\d/.test(m)) return false
	const gpt = m.match(/^gpt-(\d+)/)
	if (gpt && Number(gpt[1]) >= 5) return false
	return true
}
